/**
 * <product-description>
 * PDP "Details" accordion item: bullet point list + product description.
 *
 * - Variant switching: update(variant) swaps in the related product's
 *   point list + description (or falls back to the main product's), mirroring
 *   what product-description-new.liquid renders server-side on first paint.
 * - Public API:
 *     update(variant)
 */

class ProductDescription extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector("[data-pd-content]");
    this.trigger = this.querySelector("[data-pd-trigger]");
    this.points = this.querySelector("[data-pd-points]");
    this._variantChangedHandler = (event) => {
      const variant = event?.detail?.variant;
      if (variant) this.update(variant);
    };
    window.addEventListener("enroute:variant-changed", this._variantChangedHandler);
  }

  disconnectedCallback() {
    window.removeEventListener("enroute:variant-changed", this._variantChangedHandler);
  }

  /**
   * Point the details block at the copy for `variant`. Driven by the variant
   * switcher's enroute:variant-changed event; also callable directly.
   *
   * @param {object} variant - a variant entry from product-info-json
   */
  update(variant) {
    this.updateDescription(variant);
    this.updatePointList(variant);
  }

  updateDescription(variant) {
    if (!this.content) return;

    const info = this.productInfo();
    const related = this.related(variant, info);
    const description = related?.description || info?.description || "";
    // Nothing to show: keep whatever was rendered server-side (the
    // custom.temporary_desc fallback isn't in product-info-json).
    if (!description) return;

    this.content.innerHTML = description;
    // The trigger starts disabled when the product ships without a description;
    // now that there is one, the accordion arrow should come back.
    this.trigger?.classList.remove("disabled");
  }

  updatePointList(variant) {
    if (!this.points) return;

    const info = this.productInfo();
    const related = this.related(variant, info);
    let list = [];
    if (related?.point_list?.length > 0) {
      list = related.point_list;
    } else if (info?.point_list?.length > 0) {
      list = info.point_list;
    }

    this.points.innerHTML = list
      .map((point) => `<div class="point-item">${point}</div>`)
      .join("");
  }

  productInfo() {
    if (this.productInfoCache === undefined) {
      const scope = this.closest("section.product.wp.new-look") || document;
      this.productInfoCache = window._erj_?.getProductInfo?.(scope) || null;
    }
    return this.productInfoCache;
  }

  related(variant, info) {
    const relatedKey = variant?.related_product_key;
    return relatedKey ? info?.related_products?.[relatedKey] : null;
  }
}

if (!window.customElements.get("product-description")) {
  window.customElements.define("product-description", ProductDescription);
}
